import type { InsuranceProductVersionWithDateFormat, PricingVersionTuple } from '../../model/model.ts'

type MrpwPricingVersionFormat = `mrpw-${number}${`.${number}` | ''}-${InsuranceProductVersionWithDateFormat}`

export const mrpwPricingVersions = [
  ['mrpw-1-2024-11-19', { label: 'MRPW v1 (pre-cat-nat-update)' }],
  ['mrpw-1.2-2024-11-19', { label: 'MRPW v1.2 (pre-cat-nat-update)' }],
  ['mrpw-2-2024-11-19', { label: 'MRPW v2 (pre-cat-nat-update)' }],
  /**
   * mrpw-2.1-2025-03-23 History
   *
   * On 01/01/2025 at midnight, Wakam forcefully pushed an update to their V6 API
   * to account for the CAT NAT increase, without coordinating with us.
   *
   * As of 20/01/2025, we have two issues:
   * 1. Renewal
   *    - Since there is no change in the pricing version, nothing special happens
   *      when creating a renewal.
   * 2. 2024 Contract Endorsements
   *    - When we create an endorsement on a contract signed in 2024, it applies
   *      the CAT NAT increase even though it shouldn’t.
   *
   * At our request, Wakam provided an API V7, which implements the pricing prior
   * to the CAT NAT increase.
   *
   * Approach to solving these issues:
   *
   * 1. Solve the renewal issue by creating a new Wakam pricing version
   *    "mrpw-2.1-2025-03-23" that:
   *    - Points to Wakam API V6 (the post-CAT NAT increase API).
   *    - Is effective from 23/03/2025 (must be in the future but, late enough to warn customers in advance).
   *
   * 2. Solve the 2024 endorsements issue by:
   *    - Pointing all old Wakam pricing versions to the V7 API
   *      (the pre-CAT NAT increase API).
   *    - Modifying previous pricing versions so they use the V6 API
   *      (with the CAT NAT increase) for subscriptions whose initial signature price
   *      was calculated after 01/01/2025.
   *    - This last detail is necessary so that an endorsement on a contract signed
   *      between 01/01/2025 and 01/23/2025 does not trigger a price recalculation.
   *
   * Note : later the V6 API was replaced by the V8 api to support renewal
   */
  ['mrpw-2.1-2025-03-23', { label: 'MRPW v2.1 (post-cat-nat-update)' }],
  /**
   * Replace the V8 API by the V10 API
   * Applicable from 24/06/2025 only for new business
   *
   * From 24/06/2025, the WAKAM API will be split into two APIs:
   * - one for new business
   * - one for renewals
   *
   * This version will integrate several changes:
   * - The fire tax increase
   * - The interruption of activity tax increase
   * - The multirisque increase
   */
  ['mrpw-3.0-2025-06-24', { label: 'MRPW v3.0 (new business)' }],
  /**
   * Replace the V8 API by the V9 API
   * Applicable from 01/07/2025 only for renewals
   *
   * This version will integrate several changes:
   * - The fire tax increase
   * - The interruption of activity tax increase
   * - The multirisque increase
   */
  ['mrpw-3.1-2025-07-01', { label: 'MRPW v3.1 (renewal)' }],
  /**
   * Replace the V10 API by the V12 API
   * Applicable from 05/02/2026 only for new business
   *
   * This version integrates a +5 % increase on the price
   */
  ['mrpw-4.0-2026-02-12', { label: 'MRPW v4.0 (new business)' }],
  /**
   * Replace the V9 API by the V11 API
   * Applicable from 05/02/2026 only for renewals
   *
   * This version should not update the price for renewals and including previous v11 renewal updates
   */
  ['mrpw-4.1-2026-02-12', { label: 'MRPW v4.1 (renewal)' }],
] as const satisfies PricingVersionTuple<MrpwPricingVersionFormat>[]

export type MrpwPricingVersion = (typeof mrpwPricingVersions)[number][0]

const mrpwNewContractPricingVersions = [
  'mrpw-1-2024-11-19',
  'mrpw-1.2-2024-11-19',
  'mrpw-2-2024-11-19',
  'mrpw-2.1-2025-03-23',
  'mrpw-3.0-2025-06-24',
  'mrpw-4.0-2026-02-12',
] as const satisfies readonly MrpwPricingVersion[]

// This is the latest pricing version for new contracts: it is used to check nathan exclusions for new mrp contracts
export const latestMrpwPricingVersionForNewContract: MrpwPricingVersion =
  mrpwNewContractPricingVersions.at(-1) ?? mrpwNewContractPricingVersions[0]
